(ns ui.editors.labelled-input.view
  (:require
   [common.currencies :as currencies]
   [common.util :as cu]
   [ui.editors.data-input :as data-input]
   [ui.util :as uu]))

(defn label-elem
  [{:keys [label mandatory? class]}]
  [:label
   {:class (uu/cm "text-[14px] text-input-light-text font-semibold m-0"
                  class)}
   [:span {:class "min-w-0"}
    label
    (when mandatory?
      [:span
       {:class "ml-1 text-danger-light-text font-bold"}
       "*"])]])

;; Pure action icons only. Never edit/save state: in the new input system saves
;; are form-level or on-blur, so the old field-editor's edit/save/cancel state
;; machine has no meaning here. Do not extend without discussion.
(def tool-icon
  {:tool-item/copy     "fa-copy"
   :tool-item/settings "fa-cog"
   :tool-item/open-url "fa-external-link"})

(defn tool-button
  [{:keys [icon on-click disabled? tooltip]}]
  [:li
   {:class (str "inline-flex items-center justify-center cursor-pointer "
                "text-base-light-text hover:text-base-regular-text-accent "
                "p-1 rounded text-sm "
                (when disabled? "opacity-50 pointer-events-none"))
    :title tooltip
    :tab-index -1
    :on-mouse-down (fn [e]
                     (.preventDefault e)
                     (when (and (fn? on-click) (not disabled?))
                       (on-click e)))}
   [:i {:class (str "fal " (or (tool-icon icon) icon))}]])

(defn tools-bar
  [tools]
  [:ul {:class (str "flex flex-row gap-1 absolute right-0 top-0 m-0 p-0 list-none "
                    "opacity-0 group-hover/field-actions:opacity-100 transition-opacity")}
   (map-indexed
    (fn [i tool]
      ^{:key i} [tool-button tool])
    tools)])

(defn with-actions
  "Wraps a field, rendering pure action icons at its top-right on hover.

   The actions are independent of the wrapped field: they receive no input
   state and cannot change the value. Pass the field as the second argument:

     [with-actions
      {:tools (clipboard/copy-tool value options)}
      [view {:label \"Teams\" ...}]]

   Composes with any field, not just `view` - `currency-view` and a bare
   `data-input/view` work the same way."
  [{:keys [tools class]} field]
  [:div
   {:class (uu/cm "relative group/field-actions" class)}
   field
   (when (seq tools)
     [tools-bar tools])])

(defn input-height-cls
  [data-type]
  (case data-type
    :data.type/text-multiline
    ""
    :data.type/text-richtext
    ""
    :data.type/boolean
    ""
    ""))

(defn currency-view
  [{:keys [label mandatory? class label-class on-focus on-blur on-change data-type error
           secondary-args
           horizontal? reverse? dom-id] :as args}]
  [:div
   (merge {:class (uu/cm "flex relative gap-1.5"
                         (cond (and horizontal? reverse?)
                               "flex-row-reverse justify-end"
                               horizontal?
                               "flex-row"
                               reverse?
                               "flex-col-reverse justify-end"
                               :else
                               "flex-col"))}
          (when-not (cu/blank-value? dom-id)
            {:id (str dom-id)}))
   [label-elem
    {:label      label
     :mandatory? mandatory?
     :class      label-class}]
   [:div
    {:class (uu/cm "flex gap-1"
                   (when reverse?
                     "flex-row-reverse"))}
    [data-input/view
     (assoc secondary-args
            :options currencies/value-options)]
    [data-input/view
     (assoc args
            :class (uu/cm (input-height-cls data-type)
                          "flex-grow"
                          (when error
                            "border-danger-regular-border-paler hover:border-danger-regular-border focus:border-danger-regular-border")
                          "min-w-0"
                          class)
            :full-width? true
            :on-focus (fn [e]
                        (when (fn? on-focus)
                          (on-focus e)))
            :on-blur (fn [e]
                       (when (fn? on-blur)
                         (on-blur e)))
            :on-change (fn [& args]
                         (when (fn? on-change)
                           (apply on-change args))))]]])

(defn view
  [{:keys [label mandatory? container-class class label-class on-focus on-blur on-change data-type error
           horizontal? reverse? dom-id] :as args}]
  [:div
   (merge {:class (uu/cm "flex relative gap-1.5"
                         (cond (and horizontal? reverse?)
                               "flex-row-reverse justify-end"
                               horizontal?
                               "flex-row"
                               reverse?
                               "flex-col-reverse justify-end"
                               :else
                               "flex-col")
                         container-class)}
          (when-not (cu/blank-value? dom-id)
            {:id (str dom-id)}))
   [label-elem
    {:label      label
     :mandatory? mandatory?
     :class      label-class}]
   [data-input/view
    (assoc args
           :class (uu/cm class
                         (input-height-cls data-type)
                         (when error
                           "border-danger-regular-border-paler hover:border-danger-regular-border focus:border-danger-regular-border"))
           :data-type (or (:data-type args)
                          (when-let [editor-type (:editor-type args)]
                            (case editor-type
                              :editor.type/select :data.type/ref-single
                              :editor.type/hex-color :data.type/text-simple
                              :editor.type/toggle :data.type/boolean
                              :editor.type/search :data.type/ref-single
                              :editor.type/multi-select :data.type/ref-multi
                              :editor.type/checkbox :data.type/boolean)))
           :full-width? true
           :on-focus (fn [e]
                       (when (fn? on-focus)
                         (on-focus e)))
           :on-blur (fn [e]
                      (when (fn? on-blur)
                        (on-blur e)))
           :on-change (fn [& args]
                        (when (fn? on-change)
                          (apply on-change args))))]])
